function requiredProductionValue(name: string, value: string | undefined): string {
  const normalized = value?.trim() ?? '';
  if (!normalized && import.meta.env.PROD) {
    throw new Error(`Missing required production environment variable: ${name}`);
  }
  return normalized;
}

const apiUrl =
  requiredProductionValue('VITE_API_URL', import.meta.env.VITE_API_URL) ||
  'http://localhost:3001';
const supabaseUrl = requiredProductionValue(
  'VITE_SUPABASE_URL',
  import.meta.env.VITE_SUPABASE_URL
);
const supabaseAnonKey = requiredProductionValue(
  'VITE_SUPABASE_ANON_KEY',
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

export const config = {
  // API Configuration
  apiUrl: apiUrl.replace(/\/$/, ''),
  apiVersion: 'v1',

  // Supabase Configuration
  supabase: {
    url: supabaseUrl.replace(/\/$/, ''),
    anonKey: supabaseAnonKey,
  },

  // App Configuration
  app: {
    name: 'WareHouse Cruise Line Edition',
    version: '1.0.0',
    description: 'Modern Warehouse Management System for Cruise Lines',
    // product owner's logo (masters and icon build: design/brand/)
    logo: {
      /** 64 px: 26-32 px marks (sidebar, design boards) */
      mark: '/brand/logo-64.png',
      /** 128 px: 40-48 px marks, and the 32 px mark on 4x screens */
      markLarge: '/brand/logo-128.png',
      /** 112 px animated WebP, plays once (login) */
      animated: '/brand/logo-animated.webp',
    },
  },

  // Feature Flags
  features: {
    realtimeUpdates: true,
    lotTracking: true,
    allergenTracking: true,
    deliveryManagement: true,
    analytics: true,
  },

  // Pagination Defaults
  pagination: {
    defaultPageSize: 20,
    pageSizeOptions: [10, 20, 50, 100],
  },

  // Date Formats
  dateFormats: {
    display: 'MMM dd, yyyy',
    displayWithTime: 'MMM dd, yyyy HH:mm',
    input: 'yyyy-MM-dd',
    api: "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'",
  },

  // Order Status Configuration
  orderStatuses: {
    draft: { label: 'Draft', color: 'neutral' },
    pending: { label: 'Pending', color: 'warning' },
    confirmed: { label: 'Confirmed', color: 'primary' },
    processing: { label: 'Processing', color: 'primary' },
    picking: { label: 'Picking', color: 'primary' },
    packed: { label: 'Packed', color: 'primary' },
    ready: { label: 'Ready for Delivery', color: 'success' },
    in_transit: { label: 'In Transit', color: 'primary' },
    delivered: { label: 'Delivered', color: 'success' },
    cancelled: { label: 'Cancelled', color: 'danger' },
  } as const,

  // Inventory Configuration
  inventory: {
    lowStockThreshold: 10,
    criticalStockThreshold: 5,
    expiryWarningDays: 7,
  },

  // Allergens
  allergens: [
    { id: 'gluten', name: 'Gluten', icon: 'wheat' },
    { id: 'dairy', name: 'Dairy', icon: 'milk' },
    { id: 'eggs', name: 'Eggs', icon: 'egg' },
    { id: 'fish', name: 'Fish', icon: 'fish' },
    { id: 'shellfish', name: 'Shellfish', icon: 'shrimp' },
    { id: 'nuts', name: 'Tree Nuts', icon: 'nut' },
    { id: 'peanuts', name: 'Peanuts', icon: 'peanut' },
    { id: 'soy', name: 'Soy', icon: 'soy' },
    { id: 'sesame', name: 'Sesame', icon: 'sesame' },
    { id: 'sulfites', name: 'Sulfites', icon: 'sulfite' },
  ] as const,

  // Warehouse Zones
  warehouseZones: [
    { id: 'dry', name: 'Dry Storage', temperature: null },
    { id: 'cold', name: 'Cold Storage', temperature: '2-4C' },
    { id: 'frozen', name: 'Frozen Storage', temperature: '-18C' },
    { id: 'produce', name: 'Produce', temperature: '4-8C' },
    { id: 'beverage', name: 'Beverage', temperature: '10-15C' },
  ] as const,
} as const;

export type OrderStatus = keyof typeof config.orderStatuses;
export type AllergenId = (typeof config.allergens)[number]['id'];
export type WarehouseZoneId = (typeof config.warehouseZones)[number]['id'];

export default config;
